/**
 * Handles chunk loading errors that occur when the app is updated
 * and old chunks are no longer available
 */

// Track if we've already reloaded to prevent infinite loops
const RELOAD_KEY = 'dnceclub_chunk_reload';
const BOUNDARY_RELOAD_KEY = 'dnceclub_chunk_reload_boundary';
const VERSION_KEY = 'dnceclub_app_version';

// Generate a unique version for this build
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || Date.now().toString();

/**
 * Check if a reload has already been attempted for this error
 */
function hasReloadedRecently(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_KEY);
  if (!lastReload) return false;
  
  // Consider reload "recent" if it was within the last 10 seconds
  const timeSinceReload = Date.now() - parseInt(lastReload, 10);
  return timeSinceReload < 10000;
}

/**
 * Mark that we've attempted a reload
 */
function markReloadAttempt(): void {
  sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
}

/**
 * Check if the app version has changed
 */
export function hasAppVersionChanged(): boolean {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  return storedVersion !== null && storedVersion !== APP_VERSION;
}

/**
 * Store the current app version
 */
export function storeAppVersion(): void {
  localStorage.setItem(VERSION_KEY, APP_VERSION);
}

const CHUNK_ERROR_PATTERNS = [
  // Chrome/Firefox: "Failed to fetch dynamically imported module"
  'failed to fetch dynamically imported module',
  // Safari/iOS: "error loading dynamically imported module"
  'error loading dynamically imported module',
  // Safari variant: "loading chunk X failed"
  'loading chunk',
  // MIME type errors (can occur with bad cache or when SPA fallback returns HTML for a missing chunk)
  'is not a valid javascript mime type',
  // Generic module import failures
  'importing a module script failed',
  // Network errors during chunk load
  'failed to load module script',
  // Safari chunk mismatch: stale module map references renamed/removed exports
  'importing binding name',
  // Chrome chunk mismatch: stale module references an export the new build renamed/removed
  'does not provide an export named',
  // React's own error when lazy element type is invalid
  'lazy element type must resolve to',
  // Our own thrown error from lazyWithRetry when module.default is undefined
  'lazy component resolved to undefined',
  'chunk mismatch after deploy',
] as const;

export function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase();
  return (
    error.name === 'ChunkLoadError' ||
    CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern))
  );
}

/**
 * Reload the current URL with a cache-busting query param so the browser and any
 * upstream CDN can't serve a stale HTML document that still references deleted chunks.
 * `location.replace` keeps the URL out of history.
 */
function reloadWithCacheBust(): void {
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Handle dynamic import errors
 */
export function handleChunkLoadError(error: Error): void {
  if (!isChunkLoadError(error) || hasReloadedRecently()) {
    return;
  }

  console.warn('Chunk load error detected, reloading page to fetch latest version...');
  markReloadAttempt();

  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }
  reloadWithCacheBust();
}

/**
 * Clear the reload marker once the app has successfully mounted. This prevents the
 * error boundary from treating unrelated chunk errors (e.g. a transient network
 * failure on a later navigation) as a "reload loop" and showing "Update required".
 */
export function clearChunkReloadMarker(): void {
  sessionStorage.removeItem(RELOAD_KEY);
  sessionStorage.removeItem(BOUNDARY_RELOAD_KEY);
}

/**
 * Setup global error handler for chunk load errors
 */
export function setupChunkErrorHandler(): void {
  // Store the current version
  storeAppVersion();

  // Handle unhandled promise rejections (where most chunk errors surface)
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error) {
      handleChunkLoadError(event.reason);
    }
  });

  // Handle regular errors
  window.addEventListener('error', (event) => {
    if (event.error instanceof Error) {
      handleChunkLoadError(event.error);
    }
  });

}

/**
 * Wrap lazy imports with error handling
 */
export function lazyWithRetry<T extends React.ComponentType>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  delay = 1000
): Promise<{ default: T }> {
  return new Promise((resolve, reject) => {
    const attemptImport = (attempt: number) => {
      importFn()
        .then((module) => {
          // Chunk loaded but named export is missing — stale chunk from a previous deploy
          if (!module.default) {
            throw new Error('Lazy component resolved to undefined — chunk mismatch after deploy');
          }
          resolve(module);
        })
        .catch((error) => {
          console.warn(`Failed to load chunk (attempt ${attempt}/${retries}):`, error.message);
          
          if (attempt >= retries) {
            // After all retries failed, try one final reload if we haven't already
            handleChunkLoadError(error);
            reject(error);
          } else {
            // Exponential backoff
            setTimeout(() => attemptImport(attempt + 1), delay * attempt);
          }
        });
    };
    
    attemptImport(1);
  });
}